import { Injectable, computed, inject, signal } from "@angular/core";

import { AppStorageService } from "shared-service/app-storage/app.storage";
import { LoggerService } from "shared-service/services/logger/logger.service";
import { VlpGrpcClients } from "shared-service/services/grpc/grpc-client.factory";
import { ExternalAccountIndividualRPCClient } from "shared-grpc-api/shared/external-apis/vlp-account/vlp-account-individual-external-apis.client";

/**
 * Nav favorites (ghim menu yêu thích) roaming qua backend.
 * - Nguồn chuẩn: server (RPC GetMyNavFavorites/SaveMyNavFavorites, per-user theo JWT).
 * - Cache localStorage theo accountId để hiển thị tức thì khi mở app, rồi đồng bộ server ngầm.
 * - Cập nhật lạc quan (optimistic) + cờ dirty để lần fetch server không đè thao tác vừa toggle.
 */
@Injectable({ providedIn: "root" })
export class NavFavoritesService {
    // -----------------------------------------------------------------------------------------------------
    // @ Private fields — injected deps, reactive signals & per-user cache keys
    // -----------------------------------------------------------------------------------------------------
    private readonly storage = inject(AppStorageService);
    private readonly logger = inject(LoggerService);
    private readonly grpc = inject(VlpGrpcClients);

    private readonly _routes = signal<string[]>([]);
    /** Danh sách route đã ghim (readonly signal). */
    readonly routes = this._routes.asReadonly();
    readonly hasFavorites = computed(() => this._routes().length > 0);

    private _client?: ExternalAccountIndividualRPCClient;
    private get client(): ExternalAccountIndividualRPCClient {
        return (this._client ??= new ExternalAccountIndividualRPCClient(this.grpc.account()));
    }

    private _loadedForUser: string | null = null;
    private _dirty = false;

    private get userId(): string {
        return String(this.storage.accountId || 0);
    }

    private get cacheKey(): string {
        return `lul.nav-favorites.${this.userId}`;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /** Nạp yêu thích cho user hiện tại: cache trước (tức thì) rồi fetch server (nguồn chuẩn). */
    load(): void {
        const uid = this.userId;
        if (!uid || uid === "0") {
            return;
        }
        if (this._loadedForUser === uid) {
            return;
        }
        this._loadedForUser = uid;
        this._dirty = false;

        this._routes.set(this.readCache());
        void this.fetchFromServer();
    }

    isFavorite(route: string): boolean {
        return !!route && this._routes().includes(route);
    }

    /** Ghim/bỏ ghim 1 route: cập nhật lạc quan + lưu cache + đẩy server ngầm. */
    toggle(route: string): void {
        if (!route) {
            return;
        }
        const current = this._routes();
        const next = current.includes(route) ? current.filter((response) => response !== route) : [...current, route];
        this._routes.set(next);
        this._dirty = true;
        this.writeCache(next);
        void this.saveToServer(next);
    }

    /** Xoá state khi logout. */
    reset(): void {
        this._routes.set([]);
        this._loadedForUser = null;
        this._dirty = false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods — server sync & localStorage cache helpers
    // -----------------------------------------------------------------------------------------------------

    private async fetchFromServer(): Promise<void> {
        try {
            const resp = await this.client.getMyNavFavorites({}).response;
            // Người dùng có thể vừa toggle trong lúc đang fetch → không đè thao tác cục bộ.
            if (this._dirty) {
                return;
            }
            const routes = resp.routes ?? [];
            this._routes.set(routes);
            this.writeCache(routes);
        } catch (error) {
            this.logger?.error?.("Nạp nav favorites thất bại", { error: (error as any)?.message || error });
        }
    }

    private async saveToServer(routes: string[]): Promise<void> {
        try {
            const resp = await this.client.saveMyNavFavorites({ routes }).response;
            const saved = resp.routes ?? routes;
            this._routes.set(saved);
            this.writeCache(saved);
            this._dirty = false;
        } catch (error) {
            this.logger?.error?.("Lưu nav favorites thất bại", { error: (error as any)?.message || error });
        }
    }

    private readCache(): string[] {
        try {
            const raw = localStorage.getItem(this.cacheKey);
            if (!raw) {
                return [];
            }
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
        } catch {
            return [];
        }
    }

    private writeCache(routes: string[]): void {
        try {
            localStorage.setItem(this.cacheKey, JSON.stringify(routes));
        } catch {
            /* localStorage đầy / bị chặn → bỏ qua, server vẫn là nguồn chuẩn */
        }
    }
}
